// @ts-nocheck
import React, { useState, useMemo, useCallback, memo } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import constant from "@utils/constant";
import { useAppContext } from "@context/AppContext";

/**
 * Catalogue Component - Interactive catalog for selecting NFT attributes
 * Supports background colors, patterns, and layer choices with rarity indicators
 */
const Catalogue = memo(({
  category = "",
  identifier = "",
  data = [],
  setUserSelection,
  selectedItems = {},
  title = "",
  rarityList = [],
  setFinalTooltipText,
  rarityData = {},
  setRarityData,
}) => {
  const [tooltipText, setTooltipText] = useState("");
  const [showRarityBox, setShowRarityBox] = useState(false);
  const { avaxPrice } = useAppContext();
  const PRICE_DECIMALS = 4;
  // Helper function to get rarity type constant
  const getRarityTypeConstant = useCallback((type) => {
    switch (type) {
      case "background":
        return constant.BACKGROUND_COLOR;
      case "pattern":
        return constant.PATTERN;
      case "layerchoice":
        return constant.LAYER_CHOICE;
      default:
        return "";
    }
  }, []);

  // Memoized rarity data processing to prevent recalculation
  const rarityByType = useMemo(() => {
    if (!rarityList || !Array.isArray(rarityList)) {
      return { background: [], pattern: [], layerchoice: [] };
    }

    const result = { background: [], pattern: [], layerchoice: [] };

    rarityList.forEach((rarity) => {
      const rarityItem = {
        type: getRarityTypeConstant(rarity.rarityType),
        id: rarity.idOfType,
        start: rarity.startsOn,
        end: rarity.endsOn,
        remainingCounter: Math.max(rarity.maxCount - rarity.currentCount, 0),
        totalAllowed: rarity.maxCount,
      };

      if (rarity.rarityType === "background") {
        result.background.push(rarityItem);
      } else if (rarity.rarityType === "pattern") {
        result.pattern.push(rarityItem);
      } else if (rarity.rarityType === "layerchoice") {
        result.layerchoice.push(rarityItem);
      }
    });

    return result;
  }, [rarityList]);

  // Memoized date formatter
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return null;

    const date = new Date(timestamp * 1000);
    const year = date.getFullYear().toString().slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    const hours = ("0" + date.getHours()).slice(-2);
    const minutes = ("0" + date.getMinutes()).slice(-2);

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }, []);

  // Memoized style getter for items
  const getConditionalStyle = useCallback((item, index) => {
    switch (category) {
      case "background":
        return { backgroundColor: item.colorHexCode || "#000" };
      case "pattern":
        return {
          backgroundImage: `url(${item.thumbnailURL})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        };
      case "layerchoice":
        return {
          backgroundImage: `url(${item.layerPreviewURL})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        };
      default:
        return {};
    }
  }, [category]);

  // Check if item has rarity
  const hasRarity = useCallback((item) => {
    const rarityArray = rarityByType[category] || [];
    const itemId = getItemId(item);
    return rarityArray.some((rarity) => rarity.id === itemId);
  }, [rarityByType, category]);

  // Get item ID based on category
  const getItemId = useCallback((item) => {
    switch (category) {
      case "background":
        return item.backgroundId;
      case "pattern":
        return item.patternId;
      case "layerchoice":
        return item.layerChoiceId;
      default:
        return null;
    }
  }, [category]);

  // Check if item is selected
  const isSelected = useCallback((item) => {
    if (!selectedItems[identifier]) return false;

    const itemId = getItemId(item);
    const selectedItemId = getItemId(selectedItems[identifier]);

    return itemId && itemId === selectedItemId;
  }, [selectedItems, identifier, getItemId]);

  // Update tooltip with rarity information
  const updateTooltip = useCallback((item) => {
    const rarityArray = rarityByType[category] || [];
    const itemId = getItemId(item);
    const rarityItem = rarityArray.find((rarity) => rarity.id === itemId);

    if (!rarityItem) {
      setTooltipText("");
      setShowRarityBox(false);
      return;
    }

    const { remainingCounter, totalAllowed, type, start, end } = rarityItem;
    let tooltipMsg = `Rarity of this ${type}:`;

    const startDate = formatDate(start);
    const endDate = formatDate(end);

    if (startDate && endDate) {
      tooltipMsg += `<br>- Mint from ${startDate} to ${endDate}.`;
    } else if (endDate) {
      tooltipMsg += `<br>- Minting ends on ${endDate}.`;
    }

    if (remainingCounter > 0) {
      tooltipMsg += `<br>- Only ${remainingCounter} of ${totalAllowed} left.`;
    }

    setTooltipText(tooltipMsg);
    setShowRarityBox(true);
  }, [rarityByType, category, getItemId, formatDate]);

  // Hide rarity tooltip
  const hideRarityDetails = useCallback(() => {
    setTooltipText("");
    setShowRarityBox(false);
  }, []);

  // Update rarity data for selected item
  const updateRarityData = useCallback((item) => {
    const rarityArray = rarityByType[category] || [];
    const itemId = getItemId(item);
    const rarityItem = rarityArray.find((rarity) => rarity.id === itemId);

    const updatedRarityData = { ...rarityData };

    if (rarityItem) {
      updatedRarityData[identifier] = {
        start: rarityItem.start,
        end: rarityItem.end,
        remainingCounter: rarityItem.remainingCounter,
        totalAllowed: rarityItem.totalAllowed,
        type: title || rarityItem.type,
      };
    } else {
      updatedRarityData[identifier] = {
        start: 0,
        end: 0,
        remainingCounter: 0,
        totalAllowed: 0,
        type: "",
      };
    }

    setRarityData(updatedRarityData);
  }, [rarityByType, category, getItemId, rarityData, identifier, title, setRarityData]);

  // Handle item selection
  const updateSelection = useCallback((item) => {
    const selectedItem = { ...item, category };

    if (category === "pattern") {
      // Clear layer choices when pattern is selected
      const updatedSelection = { ...selectedItems };
      Object.keys(updatedSelection).forEach((key) => {
        if (updatedSelection[key]?.category === "layerchoice") {
          delete updatedSelection[key];
        }
      });
      updatedSelection.pattern = selectedItem;
      setUserSelection({ __replace: true, ...updatedSelection });
    } else {
      setUserSelection({ [identifier]: selectedItem });
    }

    updateRarityData(selectedItem);
  }, [category, selectedItems, identifier, setUserSelection, updateRarityData]);

  // Show price for background items
  const renderPrice = useCallback((item) => {
    if (category === "background" && item.price) {
      const priceValue = parseFloat(item.price);
      const priceUSD = (!isNaN(priceValue) && avaxPrice)
        ? (priceValue * avaxPrice).toFixed(2)
        : '...';

      return (
        <>
          <h4 className="bg-price">{`${item.price} AVAX`}</h4>
          <span className="price-dynamic-mint">${priceUSD}</span>
        </>
      );
    }
    return null;
  }, [category, avaxPrice]);

  // Memoized swiper settings
  const swiperSettings = useMemo(() => ({
    slidesPerView: 5,
    spaceBetween: 5,
    slidesPerGroup: 4,
    navigation: true,
    modules: [Navigation],
    className: "mySwiper",
    breakpoints: {
      320: { slidesPerView: 2, slidesPerGroup: 2 },
      640: { slidesPerView: 3, slidesPerGroup: 2 },
      768: { slidesPerView: 4, slidesPerGroup: 3 },
      1024: { slidesPerView: 5, slidesPerGroup: 4 },
    },
  }), []);

  // Early return if no data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  return (
    <div className="catalogue-container">
      <h3 className="mint__step-title">{title}</h3>

      <div className="mint__bg-box">
        <Swiper {...swiperSettings}>
          {data.map((item, index) => (
            <SwiperSlide key={`${category}-${index}`} style={{ padding: "3rem" }}>
              <div className="bg-palette__info">
                <div
                  className={`bg-box__wrapper ${
                    isSelected(item) ? "bg-box__wrapper--selected" : ""
                  }`}
                >
                  <div
                    className="bg-box"
                    style={getConditionalStyle(item, index)}
                    onClick={() => updateSelection(item)}
                    onMouseEnter={() => updateTooltip(item)}
                    onMouseLeave={hideRarityDetails}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select ${category} option ${index + 1}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        updateSelection(item);
                      }
                    }}
                  >
                    {hasRarity(item) && (
                      <span className="rare-label" aria-label="Rare item">
                        Rare
                      </span>
                    )}
                  </div>
                </div>
                {renderPrice(item)}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {showRarityBox && tooltipText && (
        <div
          className="rarity-box"
          role="tooltip"
          aria-live="polite"
        >
          <span
            className="rarity-text"
            dangerouslySetInnerHTML={{ __html: tooltipText }}
          />
        </div>
      )}
    </div>
  );
});

Catalogue.displayName = "Catalogue";

export default Catalogue;
